import { withScope } from '@sentry/core';
export const sentryStateKey = 'zsc-plugin-vite-ssr-sentry';
/**
 * Source: https://github.com/getsentry/sentry-javascript/blob/83f7ccec7298d77c862b83fce4ef6c1439c31a7e/packages/vue/src/errorhandler.ts
 * @license MIT - Copyright (c) 2019 Sentry (https://sentry.io) and individual contributors. All rights reserved.
 */
export function sentryErrorHandler(error, vm, lifecycleHook, appName) {
    const componentName = formatComponentName(vm, false);
    const trace = vm ? generateComponentTrace(vm) : '';
    const metadata = {
        componentName,
        lifecycleHook,
        trace,
    };
    if (vm) {
        // Vue2 - $options.propsData
        // Vue3 - $props
        if (vm.$options?.propsData) {
            metadata.propsData = vm.$options.propsData;
        }
        else if (vm.$props) {
            metadata.propsData = vm.$props;
        }
    }
    // Capture exception in the next event loop, to make sure that all breadcrumbs are recorded in time.
    setTimeout(() => {
        withScope(s => {
            s.setTag('appName', appName);
            s.captureException(error, {
                captureContext: { contexts: { vue: metadata } },
                mechanism: { handled: false },
            });
        });
    });
    const hasConsole = typeof console !== 'undefined';
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const message = `Error in ${lifecycleHook}: "${error && error.toString()}"`;
    if (hasConsole) {
        try {
            // eslint-disable-next-line no-console
            console.error(`[Vue warn]: ${message}${trace}`);
        }
        catch (_) { }
    }
}
/**
 * Vendored from https://github.com/vuejs/vue/blob/612fb89547711cacb030a3893a0065b785802860/src/core/util/debug.js
 * with types only changes.
 *
 * @license MIT - Copyright (c) 2013-present, Yuxi (Evan) You
 * @license MIT - Copyright (c) 2019 Sentry (https://sentry.io) and individual contributors. All rights reserved.
*/
const classifyRE = /(?:^|[-_])(\w)/g;
const classify = (str) => str.replace(classifyRE, c => c.toUpperCase()).replace(/[-_]/g, '');
const ROOT_COMPONENT_NAME = '<Root>';
const ANONYMOUS_COMPONENT_NAME = '<Anonymous>';
const repeat = (str, n) => {
    return str.repeat(n);
};
export const formatComponentName = (vm, includeFile) => {
    if (!vm) {
        return ANONYMOUS_COMPONENT_NAME;
    }
    if (vm.$root === vm) {
        return ROOT_COMPONENT_NAME;
    }
    // https://github.com/getsentry/sentry-javascript/issues/5204 $options can be undefined
    if (!vm.$options) {
        return ANONYMOUS_COMPONENT_NAME;
    }
    const options = vm.$options;
    let name = options.name ?? options._componentTag;
    const file = options.__file;
    if (!name && file) {
        const match = /([^/\\]+)\.vue$/.exec(file);
        if (match) {
            name = match[1];
        }
    }
    return ((name ? `<${classify(name)}>` : ANONYMOUS_COMPONENT_NAME) + (file && includeFile !== false ? ` at ${file}` : ''));
};
export const generateComponentTrace = (vm) => {
    if (vm && (vm._isVue || vm.__isVue) && vm.$parent) {
        const tree = [];
        let currentRecursiveSequence = 0;
        while (vm) {
            if (tree.length > 0) {
                const last = tree[tree.length - 1];
                if (last.constructor === vm.constructor) {
                    currentRecursiveSequence++;
                    vm = vm.$parent;
                    continue;
                }
                else if (currentRecursiveSequence > 0) {
                    tree[tree.length - 1] = [last, currentRecursiveSequence];
                    currentRecursiveSequence = 0;
                }
            }
            tree.push(vm);
            vm = vm.$parent;
        }
        const formattedTree = tree
            .map((vm, i) => (i === 0 ? '---> ' : repeat(' ', 5 + i * 2)) +
            (Array.isArray(vm)
                ? `${formatComponentName(vm[0])}... (${vm[1]} recursive calls)`
                : formatComponentName(vm)))
            .join('\n');
        return `\n\nfound in\n\n${formattedTree}`;
    }
    return `\n\n(found in ${formatComponentName(vm)})`;
};
//# sourceMappingURL=sentry.js.map